import { environment } from './environment';

export interface RuntimeConfiguration {
    apiUrl: string;
    auth0: {
        domain: string;
        clientId: string;
        authorizationParams: {
            audience: string;
        };
    };
    rsiServicesUrl: string;
    trinityRailMarketUrl: string;
    mixpanelToken: string;
    sentry: {
        dsn: string;
        sendDefaultPii: boolean;
        environment: string;
    };
}

interface ConfigurableEnvironment {
    apiUrl: string;
    auth0: RuntimeConfiguration['auth0'];
    rsiServicesUrl: string;
    trinityRailMarketUrl: string;
    mixpanelToken: string;
    sentry: RuntimeConfiguration['sentry'];
}

export function applyRuntimeConfiguration(
    target: ConfigurableEnvironment,
    configuration: RuntimeConfiguration,
): void {
    if (
        !configuration ||
        typeof configuration !== 'object' ||
        ![
            configuration.apiUrl,
            configuration.auth0?.domain,
            configuration.auth0?.clientId,
            configuration.auth0?.authorizationParams?.audience,
            configuration.rsiServicesUrl,
            configuration.trinityRailMarketUrl,
            configuration.mixpanelToken,
            configuration.sentry?.dsn,
            configuration.sentry?.environment,
        ].every((value) => typeof value === 'string' && value.trim().length > 0) ||
        typeof configuration.sentry?.sendDefaultPii !== 'boolean'
    ) {
        throw new Error('Runtime configuration is incomplete');
    }

    target.apiUrl = configuration.apiUrl;
    target.auth0.domain = configuration.auth0.domain;
    target.auth0.clientId = configuration.auth0.clientId;
    target.auth0.authorizationParams.audience =
        configuration.auth0.authorizationParams.audience;
    target.rsiServicesUrl = configuration.rsiServicesUrl;
    target.trinityRailMarketUrl = configuration.trinityRailMarketUrl;
    target.mixpanelToken = configuration.mixpanelToken;
    target.sentry.dsn = configuration.sentry.dsn;
    target.sentry.sendDefaultPii = configuration.sentry.sendDefaultPii;
    target.sentry.environment = configuration.sentry.environment;
}

export async function loadRuntimeConfiguration(): Promise<void> {
    if (!environment.production) {
        return;
    }

    const response = await fetch(
        new URL('runtime-config.json', document.baseURI),
        { cache: 'no-store' },
    );
    if (!response.ok) {
        throw new Error('Unable to load runtime configuration');
    }

    applyRuntimeConfiguration(
        environment,
        (await response.json()) as RuntimeConfiguration,
    );
}
